import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiSendJson } from "@/lib/sm-api";

const NICHE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "restaurante", label: "Restaurante" },
  { value: "cafeteria", label: "Cafetería / Panadería" },
  { value: "carniceria", label: "Carnicería" },
  { value: "almacen", label: "Almacén / Kiosco" },
  { value: "panaderia", label: "Panadería" },
  { value: "digital_products", label: "Productos digitales" },
  { value: "merchant", label: "Indumentaria / Moda" },
  { value: "servicios", label: "Servicios / Turnos" },
];

type DemoLeadResult = {
  tenantId: string;
  slug: string;
  publicUrl: string;
  categoriesCreated: number;
  productsCreated: number;
  aiDescription: string | null;
};

export function CreateDemoLeadDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [businessName, setBusinessName] = useState("");
  const [niche, setNiche] = useState("restaurante");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<DemoLeadResult | null>(null);

  function reset() {
    setBusinessName("");
    setNiche("restaurante");
    setContactName("");
    setContactEmail("");
    setContactPhone("");
    setReferenceUrl("");
    setResult(null);
  }

  async function handleCreate() {
    if (!businessName.trim()) {
      toast.error("Ingresá el nombre del negocio del prospecto");
      return;
    }
    setSaving(true);
    try {
      const r = await apiSendJson<DemoLeadResult>("/api/admin/demo-leads", "POST", {
        businessName: businessName.trim(),
        niche,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        referenceUrl: referenceUrl.trim() || undefined,
      });
      setResult(r);
      toast.success("Demo generada");
      onCreated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear la demo");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Generar demo para un lead</DialogTitle>
          <DialogDescription>
            Creá un workspace personalizado con catálogo de ejemplo para mostrarle a un prospecto concreto.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2 text-sm">
            <p>
              Workspace creado con {result.categoriesCreated} categorías y {result.productsCreated} productos de
              ejemplo.
            </p>
            {result.aiDescription ? (
              <p className="rounded-md border bg-secondary/40 p-2 text-xs text-muted-foreground">
                <Sparkles className="mr-1 inline h-3 w-3" />
                {result.aiDescription}
              </p>
            ) : null}
            <div className="flex items-center gap-2">
              <Input readOnly value={result.publicUrl} className="text-xs" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(result.publicUrl);
                  toast.success("Link copiado");
                }}
              >
                Copiar link
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Negocio del prospecto *</Label>
              <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Ej: Almacén Don Pepe" />
            </div>
            <div className="space-y-1">
              <Label>Rubro *</Label>
              <Select value={niche} onValueChange={setNiche}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NICHE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Contacto (nombre)</Label>
                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Contacto (email)</Label>
                <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} type="email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Contacto (teléfono)</Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>URL de referencia</Label>
                <Input value={referenceUrl} onChange={(e) => setReferenceUrl(e.target.value)} placeholder="opcional" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={() => void handleCreate()} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Crear demo
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
